import { PostScheduleFilterAction, PostScheduleFilterType } from '../../../models/post';
import React, { Reducer } from 'react';
import {
  CorePhotoFieldsFragment,
  PostItemFragment,
  CoreVideoFieldsFragment,
  PostQueueCountCategory,
  PostQueueCountResult,
  PostQueueCountSocialProfile,
  QueueContext,
  QueueFilterPostStatus,
  QueueSortOrder,
  QueueSortType,
  StartOfWeekFormatSetting,
  TimeslotDay,
  QueueFilterInput
} from '../../../models/graphql';
import { CalendarDataType, FirstPostDataType, QueueCellDataType } from '../../../models/calendar';
import { PostError } from '../../../components/cards/BasePostCard/BasePostCard.view';
import { format } from 'date-fns';
import { Void } from '../../../models/basic-objects';
import { LibrarySortType, PostQueueErrorEnum } from '../../../constants/post';
import { Buffer } from 'buffer';
import {
  PostCardCarouselMediaProps,
  PostCardSingleMediaProps
} from '../../../components/cards/PostCardMedia/PostCardMedia.view';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { utcToZonedTime } from 'date-fns-tz';
import { isNumber, isString } from 'lodash';

const postScheduleFilterReducer: Reducer<PostScheduleFilterType, PostScheduleFilterAction> = (state, action) => {
  switch (action.type) {
    case 'category': {
      return { ...state, category: action.payload.data, fetchPosts: action.payload.fetchPosts };
    }

    case 'content': {
      return { ...state, content: action.payload.data, fetchPosts: action.payload.fetchPosts };
    }

    case 'profile': {
      return { ...state, profile: action.payload.data, fetchPosts: action.payload.fetchPosts };
    }

    case 'source': {
      return { ...state, source: action.payload.data, fetchPosts: action.payload.fetchPosts };
    }

    case 'timing': {
      return { ...state, timing: action.payload.data, fetchPosts: action.payload.fetchPosts };
    }

    case 'evergreen': {
      return { ...state, evergreen: action.payload.data, fetchPosts: action.payload.fetchPosts };
    }

    case 'status': {
      return { ...state, status: action.payload.data, fetchPosts: action.payload.fetchPosts };
    }
    case 'switchFilterType': {
      return action.payload.data;
    }
    case 'resetAll':
      return {
        status: QueueFilterPostStatus.SCHEDULED,
        profile: '',
        category: '',
        timing: '',
        evergreen: '',
        source: '',
        content: '',
        resetAll: '',
        fetchPosts: true
      };
    default:
      return state;
  }
};

const initialCalendarState = (): CalendarDataType<QueueCellDataType> => {
  const hours = 23; // total number of hours, from 00 to 23 - rows
  const calendarData: CalendarDataType<QueueCellDataType> = [
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    [],
    []
  ];
  for (let i = 0; i <= hours; i++) {
    calendarData[i] = [undefined, [], [], [], [], [], [], []];
  }
  return calendarData;
};

const getLocalDateFormatted = (dateTime: Date, formatString: string): string => {
  if (!dateTime) {
    return format(new Date(), formatString);
  }
  return format(dateTime, formatString);
};

const transformPostData = (
  posts: PostItemFragment[],
  startOfWeekFormatSettings: StartOfWeekFormatSetting,
  tz: string
): CalendarDataType<QueueCellDataType> => {
  const data = initialCalendarState();

  // Define the standard order
  const standardDays: Array<TimeslotDay> = [
    TimeslotDay.SUNDAY,
    TimeslotDay.MONDAY,
    TimeslotDay.TUESDAY,
    TimeslotDay.WEDNESDAY,
    TimeslotDay.THURSDAY,
    TimeslotDay.FRIDAY,
    TimeslotDay.SATURDAY
  ];

  // Modify order based on the start of the week setting
  const days: Array<TimeslotDay | null> =
    startOfWeekFormatSettings === StartOfWeekFormatSetting.SUNDAY
      ? [null, ...standardDays]
      : [null, ...standardDays.slice(1), standardDays[0]];

  days.forEach((day, index) => {
    if (day) {
      posts.forEach(post => {
        const calculatedInTz = utcToZonedTime(`${post.calculatedUtcDatetime}Z`, tz);
        const dateFormatted = getLocalDateFormatted(calculatedInTz, 'EEEE H').toUpperCase();

        const [postDayOfWeek, postHour] = dateFormatted.split(' ');
        if (day === postDayOfWeek) {
          (data[parseInt(postHour)][index] as PostItemFragment[]).push(post);
        }
      });
    }
  });

  return data;
};

const getPostMessaging = (type: PostQueueErrorEnum, action: Void, message?: string): PostError | undefined => {
  switch (type) {
    case PostQueueErrorEnum.WORKSPACE_PAUSED:
      return {
        message: 'Your whole queue is paused.',
        actionLabel: 'Pause settings',
        action
      };
    case PostQueueErrorEnum.SOCIAL_PROFILE_CATEGORY_PAUSED:
      return {
        message: 'Posts for this Profile + Category are paused.',
        actionLabel: 'Pause settings',
        action
      };
    case PostQueueErrorEnum.CATEGORY_SEASONALLY_PAUSED:
      return {
        message: 'Category is not in season.',
        actionLabel: 'Category settings',
        action
      };
    case PostQueueErrorEnum.STATUS_UNQUEUED:
      return {
        message: message || 'No Timeslots for Category and Profile',
        actionLabel: 'Add Timeslot',
        action
      };
    case PostQueueErrorEnum.ERROR:
      return {
        message: message || '',
        actionLabel: 'Reschedule',
        action
      };
    default:
      return undefined;
  }
};

type QueueCountKey = keyof Omit<PostQueueCountResult, '__typename'>;

const isCategory = (item: PostQueueCountCategory | PostQueueCountSocialProfile): item is PostQueueCountCategory => {
  return (item as PostQueueCountCategory).categoryId !== undefined;
};

const getQueueCounts = (queueCounts: PostQueueCountResult, field: QueueCountKey) => {
  const counts: Record<string | number, number | undefined> = {};

  if (field === 'categories' || field === 'socialProfiles') {
    for (const item of queueCounts[field]) {
      let key: number;
      if (isCategory(item)) {
        key = item.categoryId;
      } else {
        key = item.socialProfileId;
      }
      counts[key] = item.count;
    }
  } else {
    const dataField = queueCounts[field] as unknown as Record<string, number>;

    for (const key in dataField) {
      if (dataField.hasOwnProperty(key)) {
        counts[key] = dataField[key];
      }
    }
  }

  return counts;
};

const mapStatusToSortType = (status: QueueFilterPostStatus | undefined): QueueSortType => {
  switch (status) {
    case QueueFilterPostStatus.FAILED:
      return QueueSortType.FAILED_TIME;
    case QueueFilterPostStatus.PAST:
      return QueueSortType.PUBLISHED_TIME;
    case QueueFilterPostStatus.PAUSED:
    case QueueFilterPostStatus.DRAFT:
    case QueueFilterPostStatus.UNQUEUED:
    case QueueFilterPostStatus.DELETED:
      return QueueSortType.ORIGINAL_POST_CREATED_TIME;
    default:
      return QueueSortType.CALCULATED_POST_TIME;
  }
};

const mapStatusToSortOrder = (sortType: QueueSortType | undefined): QueueSortOrder => {
  switch (sortType) {
    case QueueSortType.FAILED_TIME:
    case QueueSortType.PUBLISHED_TIME:
      return QueueSortOrder.DESCENDING;
    case QueueSortType.ORIGINAL_POST_CREATED_TIME:
    default:
      return QueueSortOrder.ASCENDING;
  }
};

const mapStringToStatusType = (status: string): QueueFilterPostStatus => {
  switch (status) {
    case QueueFilterPostStatus.DELETED:
      return QueueFilterPostStatus.DELETED;
    case QueueFilterPostStatus.PAST:
      return QueueFilterPostStatus.PAST;
    case QueueFilterPostStatus.SCHEDULED:
      return QueueFilterPostStatus.SCHEDULED;
    case QueueFilterPostStatus.DRAFT:
      return QueueFilterPostStatus.DRAFT;
    case QueueFilterPostStatus.FAILED:
      return QueueFilterPostStatus.FAILED;
    case QueueFilterPostStatus.UNQUEUED:
      return QueueFilterPostStatus.UNQUEUED;
    case QueueFilterPostStatus.PAUSED:
      return QueueFilterPostStatus.PAUSED;
    default:
      throw new Error('Invalid type');
  }
};

const determineSortOrder = (status: QueueFilterPostStatus) => {
  return status === QueueFilterPostStatus.PAST || status === QueueFilterPostStatus.FAILED
    ? QueueSortOrder.DESCENDING
    : QueueSortOrder.ASCENDING;
};

const determineLibraryScrollDirection = (
  scrollPosition: 'TOP' | 'BOTTOM',
  currentSortType: QueueSortType,
  librarySort: LibrarySortType
): QueueSortOrder => {
  // First, we determine the natural sort order for the currentSortType.
  const naturalOrder = Object.values(librarySort).find(entry => entry.type === currentSortType)?.order;

  // If we don't find a natural order, we can default to DESCENDING (or another fallback as per your needs).
  if (!naturalOrder) return QueueSortOrder.DESCENDING;

  // If the user is scrolling to the 'TOP' and the natural order is 'ASCENDING', or if the user is scrolling to the 'BOTTOM' and the natural order is 'DESCENDING', we fetch older records.
  if (
    (scrollPosition === 'TOP' && naturalOrder === QueueSortOrder.ASCENDING) ||
    (scrollPosition === 'BOTTOM' && naturalOrder === QueueSortOrder.DESCENDING)
  ) {
    return QueueSortOrder.DESCENDING;
  } else {
    return QueueSortOrder.ASCENDING;
  }
};

const getCursorFromPostId = (id: string | number) => {
  return Buffer.from(`${id}`).toString('base64');
};

const determineCursorFromScrollPosition = (
  posts: PostItemFragment[],
  scrollPosition: 'TOP' | 'BOTTOM'
): string | undefined => {
  const firstPost = posts[0];
  const lastPost = posts[posts.length - 1];

  if (firstPost && scrollPosition === 'TOP') {
    return getCursorFromPostId(firstPost.id);
  } else if (lastPost && scrollPosition === 'BOTTOM') {
    return getCursorFromPostId(lastPost.id);
  }
  return undefined;
};

const updatePostsAndLimits = ({
  posts,
  scrollPosition,
  fetchOrder,
  numberOfPostsToFetch,
  setCorePostItems,
  setLimitsReached
}: {
  posts: PostItemFragment[];
  scrollPosition: 'TOP' | 'BOTTOM';
  fetchOrder: QueueSortOrder;
  numberOfPostsToFetch: number;
  setCorePostItems: React.Dispatch<React.SetStateAction<PostItemFragment[]>>;
  setLimitsReached: React.Dispatch<React.SetStateAction<{ upper: boolean; lower: boolean }>>;
}) => {
  const isLimit = posts.length < numberOfPostsToFetch;

  setCorePostItems(prev => {
    const existingIds = new Set(prev.map(p => p.id)); // track existing post IDs
    const newPosts = posts.filter(p => !existingIds.has(p.id)); // only add new unique posts

    if (newPosts.length === 0) {
      return prev; // no changes if all posts are duplicates
    }

    if (fetchOrder === QueueSortOrder.ASCENDING) {
      if (scrollPosition === 'BOTTOM') {
        return [...prev, ...newPosts];
      } else {
        return [...newPosts, ...prev];
      }
    } else {
      // QueueSortOrder.DESCENDING
      if (scrollPosition === 'TOP') {
        return [...newPosts, ...prev];
      } else {
        return [...prev, ...newPosts];
      }
    }
  });

  setLimitsReached(old => ({
    ...old,
    [scrollPosition === 'BOTTOM' ? 'lower' : 'upper']: isLimit
  }));
};

type TypeNameItem = {
  __typename?: string;
  [key: string]: unknown;
};

const isPhotoFieldsFragment = (item: TypeNameItem): item is CorePhotoFieldsFragment => {
  return item && item.__typename === 'Photo';
};

const isVideoFieldsFragment = (item: TypeNameItem): item is CoreVideoFieldsFragment => {
  return item && item.__typename === 'Video';
};

// @TODO: rename to better identify as media mapping
const mapCorePostToPostCardType = (
  corePostItem: PostItemFragment,
  forceSingle?: boolean
): PostCardSingleMediaProps | PostCardCarouselMediaProps => {
  // Check if media is not null or undefined and its length
  const mediaLength = corePostItem.variationMeta?.currentVariation.media?.length || 0;

  // Map to CorePhotoFieldsFragment[] or CoreVideoFieldsFragment[]
  const intermediateMedia = corePostItem.variationMeta?.currentVariation.media
    ?.map(mediaItem => {
      if (mediaItem.__typename === 'Photo') {
        return {
          __typename: 'Photo',
          ...mediaItem,
          gif: false,
          filename: mediaItem.source
        };
      }

      if (mediaItem.__typename === 'Video') {
        return {
          __typename: 'Video',
          ...mediaItem,
          transcoded: false,
          length: null
        };
      }

      return null;
    })
    .filter(item => item !== null) as (CorePhotoFieldsFragment | CoreVideoFieldsFragment)[]; // remove any nulls

  const mappedMedia =
    intermediateMedia?.filter(item => isPhotoFieldsFragment(item) || isVideoFieldsFragment(item)) || [];

  if (mediaLength > 1 && !forceSingle) {
    return {
      type: 'Carousel',
      media: mappedMedia as CorePhotoFieldsFragment[]
    };
  }

  return {
    type: 'Single',
    media: mappedMedia as CorePhotoFieldsFragment[] | CoreVideoFieldsFragment[]
  };
};

// PostItemFragment type guard
const isPostItemFragment = (
  item:
    | {
        __typename?: string;
      }
    | undefined
): item is PostItemFragment => {
  return item !== undefined && item.__typename === 'Post';
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const purgeAllPostItemCheckboxes = (formInstance: UseFormReturn<FieldValues, any, undefined>) => {
  const currentValues = formInstance.getValues();
  const checkboxes = currentValues.postItemCheckBox;

  if (checkboxes && checkboxes.length > 0) {
    // Create a new object without postItemCheckBox
    const updatedValues = { ...currentValues, postItemCheckBox: [] };

    // This is essence unregisters the postItemCheckBox
    formInstance.reset(updatedValues);
  }
};

const getFirstPost = (posts: CalendarDataType<QueueCellDataType>): FirstPostDataType => {
  const row = posts.findIndex(row => row.find(col => col && col.length));
  const post = !!posts.find(row => row.find(cell => cell && cell.find(timeslot => timeslot)));
  const rowIndex = row > -1 ? row : 0; // todo maybe we want to scroll to current time if no time present
  return { rowIndex, post };
};

const getLastPost = (posts: CalendarDataType<QueueCellDataType>): FirstPostDataType =>
  posts.reduce(
    (prev, current, rowIndex) => {
      const hasPost = !!current.find(cell => cell && cell.find(post => post));
      return hasPost ? { post: true, rowIndex } : prev;
    },
    {
      post: false,
      rowIndex: -1
    } as FirstPostDataType
  );

const scrollToPost = (elem: HTMLTableElement, rowIndex: number): void => {
  const rows = elem.querySelectorAll('table[data-scrollable] tbody tr') as NodeListOf<HTMLTableRowElement>;
  const parentHolder = elem.parentElement as HTMLDivElement;
  const row = rows.item(rowIndex);
  const theadOffset = 48;
  const elementTop = row.getBoundingClientRect().top;
  const parentTop = parentHolder.getBoundingClientRect().top;
  const offsetPosition = elementTop - parentTop - theadOffset;
  parentHolder.scrollTo({
    top: offsetPosition,
    behavior: 'smooth'
  });
};

const getQueryContext = (status: QueueFilterPostStatus) => {
  switch (status) {
    case QueueFilterPostStatus.PAUSED:
    case QueueFilterPostStatus.DRAFT:
    case QueueFilterPostStatus.UNQUEUED:
    case QueueFilterPostStatus.DELETED:
      return QueueContext.LIBRARY;
    default:
      return QueueContext.SCHEDULE;
  }
};

const fastHash = (input: string): string => {
  let hash = 0;
  const length = input.length;

  for (let i = 0; i < length; i++) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0; // Convert to 32-bit integer
  }

  // Convert the 32-bit integer hash to a string for consistency
  return Math.abs(hash).toString(36);
};

const generateQueueFilterHash = (filter: QueueFilterInput): string => {
  const normalizedFilter = Object.entries(filter)
    .filter(([_, value]) => value !== undefined && value !== null)
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .reduce<Record<string, unknown>>((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});

  const filterString = JSON.stringify(normalizedFilter);
  return fastHash(filterString);
};

const getBitlyCodeFromLinkUrl = (linkUrl: string) => {
  const bitlyUrl = new URL(linkUrl);
  return bitlyUrl.pathname.split('/').pop(); // Extract the last part of the path
};

const getSelectedCheckboxes = (checkboxes: []): string[] => {
  const boxes: string[] = [];

  checkboxes?.forEach((v, _) => {
    if (v && (isString(v) || isNumber(v))) {
      // Make sure we only manage actual ID where the value is a string or number
      boxes.push(v);
    }
  });

  return boxes;
};

export const PostScheduleHelpers = {
  postScheduleFilterReducer,
  initialCalendarState,
  transformPostData,
  getFirstPost,
  getLastPost,
  getLocalDateFormatted,
  getPostMessaging,
  getQueueCounts,
  mapStatusToSortType,
  mapStatusToSortOrder,
  mapStringToStatusType,
  determineSortOrder,
  getCursorFromPostId,
  determineLibraryScrollDirection,
  determineCursorFromScrollPosition,
  updatePostsAndLimits,
  mapCorePostToPostCardType,
  isPostItemFragment,
  purgeAllPostItemCheckboxes,
  scrollToPost,
  getQueryContext,
  getBitlyCodeFromLinkUrl,
  generateQueueFilterHash,
  getSelectedCheckboxes
};
